import { formatDayRange } from '@radevents/shared';

/**
 * Zeitlicher Zustand eines Profi-Rennens — die eine Stelle, an der aus Start- und
 * Enddatum "laeuft gerade / kommend / abgeschlossen" wird.
 *
 * Datumsstrings sind reine Kalendertage ("2026-07-04"). Sie muessen als *lokale*
 * Mitternacht geparst werden ('T00:00:00'), nicht als UTC — sonst springt der Tag
 * rund um Mitternacht um eins.
 */

export type RaceState = 'RUNNING' | 'UPCOMING' | 'PAST';

/** Lokale Mitternacht des Kalendertags. */
function startOfDay(date: string): Date {
  return new Date(date + 'T00:00:00');
}

/** Lokales Tagesende — ein Rennen gilt bis zum Ablauf seines letzten Tages als laufend. */
function endOfDay(date: string): Date {
  return new Date(date + 'T23:59:59');
}

function today(now: Date): Date {
  const d = new Date(now.getTime());
  d.setHours(0, 0, 0, 0);
  return d;
}

export function isRunning(start: string, end: string, now: Date = new Date()): boolean {
  return startOfDay(start) <= now && endOfDay(end) >= now;
}

/** true, solange der letzte Renntag nicht vorbei ist (laufend zaehlt mit). */
export function isUpcoming(end: string, now: Date = new Date()): boolean {
  return endOfDay(end) >= now;
}

export function raceState(start: string, end: string, now: Date = new Date()): RaceState {
  if (!isUpcoming(end, now)) return 'PAST';
  return isRunning(start, end, now) ? 'RUNNING' : 'UPCOMING';
}

/**
 * "Startet heute", "In 12 Tagen", "Laeuft noch 3 Tage" — oder null fuer ein
 * abgeschlossenes Rennen, das keinen Countdown mehr hat.
 */
export function daysUntilLabel(start: string, end: string, now: Date = new Date()): string | null {
  const t = today(now);
  const startDate = startOfDay(start);
  const endDate = startOfDay(end);
  if (endDate < t) return null;
  if (startDate.getTime() === t.getTime()) return 'Startet heute';
  if (startDate <= t) {
    const daysLeft = Math.ceil((endDate.getTime() - t.getTime()) / 86400000);
    return daysLeft === 0 ? 'Heute letzter Tag' : `Läuft noch ${daysLeft} Tag${daysLeft === 1 ? '' : 'e'}`;
  }
  const days = Math.ceil((startDate.getTime() - t.getTime()) / 86400000);
  if (days === 0) return 'Startet heute';
  if (days === 1) return 'Morgen';
  return `In ${days} Tagen`;
}

/**
 * Renntag eines laufenden Rennens, z. B. { day: 3, total: 21 } → "Tag 3 von 21".
 * null, wenn das Rennen nicht laeuft oder nur einen Tag dauert (dann sagt die Zahl nichts).
 */
export function raceDayProgress(
  start: string,
  end: string,
  now: Date = new Date()
): { day: number; total: number } | null {
  if (!isRunning(start, end, now)) return null;
  const t = today(now);
  const total = Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / 86400000) + 1;
  if (total <= 1) return null;
  const day = Math.round((t.getTime() - startOfDay(start).getTime()) / 86400000) + 1;
  return { day, total };
}

/** Zeitraum eines Rennens — dieselbe Schreibweise wie in der App (`formatDayRange` in shared). */
export const formatRaceDateRange = formatDayRange;
